import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardBody, Typography, Progress, Chip } from '@material-tailwind/react';
import {
    CpuChipIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    XCircleIcon,
    Cog6ToothIcon
} from '@heroicons/react/24/outline';

const QueueMonitor = () => {
    const [queueStats, setQueueStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    useEffect(() => {
        fetchQueueStats();
        const interval = setInterval(fetchQueueStats, 5000); // Update every 5 seconds
        return () => clearInterval(interval);
    }, []);

    const fetchQueueStats = async () => {
        try {
            const response = await axios.get('/api/queue/stats');
            setQueueStats(response.data);
            setLastUpdate(new Date());
            setError(null);
        } catch (err) {
            setError(err.message);
            console.error('Failed to fetch queue stats:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const getMemoryPercentage = () => {
        if (!queueStats?.memoryUsage) return 0;
        return Math.round((queueStats.memoryUsage.heapUsed / queueStats.memoryUsage.heapTotal) * 100);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <Card className="w-full">
                <CardBody className="text-center">
                    <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <Typography variant="h6" color="red">
                        Failed to load queue statistics
                    </Typography>
                    <Typography color="gray">{error}</Typography>
                    <button
                        onClick={fetchQueueStats}
                        className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                        Retry
                    </button>
                </CardBody>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <Typography variant="h4" color="blue-gray">
                    Queue Monitor
                </Typography>
                <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <Typography variant="small" color="gray">
                            Live
                        </Typography>
                    </div>
                    {lastUpdate && (
                        <Typography variant="small" color="gray">
                            Updated: {lastUpdate.toLocaleTimeString()}
                        </Typography>
                    )}
                </div>
            </div>

            {/* System Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardBody className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Typography variant="small" color="gray">
                                    Uptime
                                </Typography>
                                <Typography variant="h6">
                                    {queueStats?.uptimeFormatted || '0s'}
                                </Typography>
                            </div>
                            <ClockIcon className="h-8 w-8 text-blue-500" />
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Typography variant="small" color="gray">
                                    Active Jobs
                                </Typography>
                                <Typography variant="h6">
                                    {queueStats?.activeJobs || 0}
                                </Typography>
                            </div>
                            <Cog6ToothIcon className="h-8 w-8 text-orange-500" />
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Typography variant="small" color="gray">
                                    Total Workers
                                </Typography>
                                <Typography variant="h6">
                                    {queueStats?.totalWorkers || 0}
                                </Typography>
                            </div>
                            <CpuChipIcon className="h-8 w-8 text-purple-500" />
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Typography variant="small" color="gray">
                                    Max Concurrency
                                </Typography>
                                <Typography variant="h6">
                                    {queueStats?.maxConcurrency || 0}
                                </Typography>
                            </div>
                            <CheckCircleIcon className="h-8 w-8 text-green-500" />
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* Memory Usage */}
            <Card>
                <CardHeader floated={false} shadow={false} className="pb-2">
                    <Typography variant="h6">Memory Usage</Typography>
                </CardHeader>
                <CardBody className="pt-0">
                    <div className="mb-4">
                        <div className="flex justify-between mb-2">
                            <Typography variant="small">
                                Heap Used: {queueStats?.memoryUsageFormatted?.heapUsed}
                            </Typography>
                            <Typography variant="small">
                                {getMemoryPercentage()}%
                            </Typography>
                        </div>
                        <Progress
                            value={getMemoryPercentage()}
                            color={getMemoryPercentage() > 80 ? 'red' : getMemoryPercentage() > 60 ? 'amber' : 'green'}
                        />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <Typography variant="small" color="gray">Heap Total</Typography>
                            <Typography>{queueStats?.memoryUsageFormatted?.heapTotal}</Typography>
                        </div>
                        <div>
                            <Typography variant="small" color="gray">RSS</Typography>
                            <Typography>{queueStats?.memoryUsageFormatted?.rss}</Typography>
                        </div>
                        <div>
                            <Typography variant="small" color="gray">External</Typography>
                            <Typography>{queueStats?.memoryUsageFormatted?.external}</Typography>
                        </div>
                        <div>
                            <Typography variant="small" color="gray">Heap Used</Typography>
                            <Typography>{queueStats?.memoryUsageFormatted?.heapUsed}</Typography>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Worker Statistics */}
            <Card>
                <CardHeader floated={false} shadow={false} className="pb-2">
                    <Typography variant="h6">Worker Performance</Typography>
                </CardHeader>
                <CardBody className="pt-0">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-max table-auto text-left">
                            <thead>
                                <tr>
                                    <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            Worker Name
                                        </Typography>
                                    </th>
                                    <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            Running
                                        </Typography>
                                    </th>
                                    <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            Total Processed
                                        </Typography>
                                    </th>
                                    <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            Success Rate
                                        </Typography>
                                    </th>
                                    <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                        <Typography variant="small" color="blue-gray" className="font-normal">
                                            Avg Duration
                                        </Typography>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {queueStats?.globalStats?.map((worker, index) => (
                                    <tr key={index}>
                                        <td className="p-4 border-b border-blue-gray-50">
                                            <Typography variant="small" color="blue-gray" className="font-medium">
                                                {worker.name}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-blue-gray-50">
                                            <Chip
                                                size="sm"
                                                variant="ghost"
                                                value={queueStats.workerStats?.find(w => w.name === worker.name)?.currentlyRunning || 0}
                                                color={queueStats.workerStats?.find(w => w.name === worker.name)?.currentlyRunning > 0 ? 'blue' : 'gray'}
                                            />
                                        </td>
                                        <td className="p-4 border-b border-blue-gray-50">
                                            <Typography variant="small" color="blue-gray">
                                                {worker.totalJobs}
                                            </Typography>
                                        </td>
                                        <td className="p-4 border-b border-blue-gray-50">
                                            <div className="flex items-center space-x-2">
                                                <Typography variant="small" color="blue-gray">
                                                    {worker.successRate}%
                                                </Typography>
                                                {worker.successRate >= 90 ? (
                                                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                                ) : worker.successRate < 70 ? (
                                                    <XCircleIcon className="h-4 w-4 text-red-500" />
                                                ) : (
                                                    <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-4 border-b border-blue-gray-50">
                                            <Typography variant="small" color="blue-gray">
                                                {worker.avgProcessingTime}ms
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardBody>
            </Card>

            {/* Error Log */}
            {queueStats?.globalStats?.some(worker => worker.lastError) && (
                <Card>
                    <CardHeader floated={false} shadow={false} className="pb-2">
                        <Typography variant="h6" color="red">Recent Errors</Typography>
                    </CardHeader>
                    <CardBody className="pt-0">
                        <div className="space-y-2">
                            {queueStats.globalStats
                                .filter(worker => worker.lastError)
                                .map((worker, index) => (
                                    <div key={index} className="bg-red-50 p-3 rounded-lg">
                                        <Typography variant="small" color="red" className="font-medium">
                                            {worker.name}
                                        </Typography>
                                        <Typography variant="small" color="gray">
                                            {worker.lastError}
                                        </Typography>
                                    </div>
                                ))}
                        </div>
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default QueueMonitor;
